import { useState, useRef } from "react";
import { Box, Typography, Button, Link as MuiLink } from "@mui/material";
import { FormikProps } from "formik";
import { imagepathReturn } from "../../config/formatters.ts";
import "../../styles/scss/Form.scss";

interface DocumentUploadProps {
  name: string;
  formik: FormikProps<any>;
  label?: string;
  accept?: string;
  maxSizeMB?: number;
}

export default function DocumentUpload({
  name,
  formik,
  label = "Upload Document",
  accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png",
  maxSizeMB = 10,
}: DocumentUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const error = formik.touched[name] && Boolean(formik.errors[name]);
  const helperText = formik.touched[name] && formik.errors[name];

  const value = formik.values[name];
  const isNewFile = value instanceof File;
  const existingUrl = !isNewFile && value ? imagepathReturn(value) : null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      formik.setFieldValue(name, file);
    }
  };

  const handleRemove = () => {
    formik.setFieldValue(name, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      formik.setFieldValue(name, file);
    }
  };

  return (
    <Box className="image-upload-area" sx={{ flexDirection: "column" }}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {!isNewFile && !existingUrl && (
        <Box
          className={`image-upload-zone ${dragActive ? "drag-active" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(false);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={handleDrop}
          sx={{ width: "100%", height: 100 }}
        >
          <Box className="upload-zone-icon">
            <span className="material-symbols-outlined">upload_file</span>
          </Box>
          <Box className="upload-zone-text">
            <p>
              Drag & drop or <span className="upload-link">browse</span>
            </p>
            <p>{label} (up to {maxSizeMB}MB)</p>
          </Box>
        </Box>
      )}

      {(isNewFile || existingUrl) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            background: "rgba(90, 68, 255, 0.04)",
            borderRadius: "8px",
            border: "1px solid rgba(0,0,0,0.08)",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "10px",
              background:
                "linear-gradient(135deg, rgba(90, 68, 255, 0.12), rgba(173, 131, 255, 0.18))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ color: "#5a44ff", fontSize: 22 }}
            >
              description
            </span>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: 13,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                mb: existingUrl ? 0.25 : 0,
              }}
            >
              {isNewFile ? value.name : "Uploaded document"}
            </Typography>
            {existingUrl && (
              <MuiLink
                href={existingUrl}
                target="_blank"
                underline="hover"
                sx={{ fontSize: 12 }}
              >
                View uploaded file
              </MuiLink>
            )}
          </Box>
          <Button
            size="small"
            onClick={handleRemove}
            sx={{ minWidth: 0, p: 0.5, color: "#ef4444" }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
              close
            </span>
          </Button>
        </Box>
      )}

      {error && (
        <Box className="form-error" sx={{ marginTop: "4px" }}>
          <span className="material-symbols-outlined">error</span>
          {helperText as string}
        </Box>
      )}
    </Box>
  );
}